import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LocateFixed, Loader2, Camera } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CoachAvatar from "@/components/CoachAvatar";

export default function CoachLocationCard() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ city: user.city || "", lat: user.lat ?? "", lng: user.lng ?? "", available: user.available !== false });
  const [locating, setLocating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/coaches/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo de profil mise à jour");
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const useGooglePhoto = async () => {
    setUploading(true);
    try {
      await api.post("/coaches/me/photo/google");
      toast.success("Photo Google appliquée");
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    finally { setUploading(false); }
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation indisponible");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm((f) => ({ ...f, lat: p.coords.latitude.toFixed(5), lng: p.coords.longitude.toFixed(5) })); setLocating(false); },
      () => { toast.error("Position refusée"); setLocating(false); }
    );
  };

  const save = async () => {
    try {
      await api.put("/coaches/me", {
        city: form.city || null,
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
        available: form.available,
      });
      refresh && refresh();
      toast.success("Position mise à jour — vous apparaissez sur la carte");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="mt-8 sc-card" data-testid="coach-location-card">
      <div className="flex items-center gap-4">
        <div className="relative">
          <CoachAvatar coach={user} size="w-20 h-20" textSize="text-2xl" />
          <button data-testid="coach-photo-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
            className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center shadow hover:bg-slate-700 disabled:opacity-50" aria-label="Changer la photo">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
          <input ref={fileRef} data-testid="coach-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Mon profil & ma position</h2>
          <p className="text-sm text-slate-600 mt-1">Une photo inspire confiance ; les clients repèrent les coachs qui bougent près de chez eux.</p>
          {user.google_picture && user.photo_url !== user.google_picture && (
            <button data-testid="use-google-photo-btn" disabled={uploading} onClick={useGooglePhoto} className="mt-2 text-xs font-semibold text-emerald-700 hover:underline">Utiliser ma photo Google</button>
          )}
        </div>
      </div>
      <div className="mt-3 grid sm:grid-cols-4 gap-2">
        <input data-testid="coach-city" placeholder="Ville" className="border border-slate-200 rounded-xl p-2 text-sm" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <input data-testid="coach-lat" placeholder="Latitude" type="number" step="0.00001" className="border border-slate-200 rounded-xl p-2 text-sm" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
        <input data-testid="coach-lng" placeholder="Longitude" type="number" step="0.00001" className="border border-slate-200 rounded-xl p-2 text-sm" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
        <button data-testid="coach-locate-btn" onClick={locate} className="sc-btn sc-btn-outline text-sm justify-center">
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />} Me localiser
        </button>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input data-testid="coach-available" type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} />
          Disponible pour de nouveaux clients
        </label>
        <button data-testid="coach-location-save" onClick={save} className="sc-btn sc-btn-emerald">Enregistrer</button>
      </div>
    </div>
  );
}
